import React, { useState, useRef, useEffect } from 'react'
import { ChartThemeManager, type ChartTheme } from '../Services/ChartThemeManager'

export interface ChartTitleProps {
  /** Chart identifier */
  chartId: string
  
  /** Current title text */
  title: string
  
  /** Visual theme for the title */
  theme?: ChartTheme
  
  /** Whether the title is editable */
  editable?: boolean
  
  /** Whether the title is currently disabled */
  disabled?: boolean
  
  /** Placeholder text when title is empty */
  placeholder?: string
  
  /** Maximum length for the title */
  maxLength?: number
  
  /** Callback when title changes */
  onTitleChange?: (chartId: string, newTitle: string) => void
  
  /** Callback when editing starts */
  onEditStart?: (chartId: string) => void
  
  /** Callback when editing ends */
  onEditEnd?: (chartId: string, finalTitle: string) => void
  
  /** Additional CSS styles */
  style?: React.CSSProperties
  
  /** Custom CSS class name */
  className?: string
}

/**
 * Editable chart title component with inline editing support
 * Allows users to click and edit chart titles directly
 */
export function ChartTitle({
  chartId,
  title,
  theme = 'dark',
  editable = true,
  disabled = false,
  placeholder = 'Untitled Chart',
  maxLength = 100,
  onTitleChange,
  onEditStart,
  onEditEnd,
  style,
  className
}: ChartTitleProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)
  const colors = ChartThemeManager.getChartColors(theme)
  const styles = ChartThemeManager.getChartStyles()

  // Update editValue when title prop changes
  useEffect(() => {
    setEditValue(title)
  }, [title])

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const baseStyles: React.CSSProperties = {
    color: colors.text.primary,
    fontSize: styles.fontSize.medium,
    fontWeight: '500',
    padding: '4px 8px',
    borderRadius: styles.borderRadius,
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    cursor: editable && !disabled ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    minWidth: '120px',
    maxWidth: '300px',
    display: 'block',
    ...style
  }

  const editingStyles: React.CSSProperties = {
    ...baseStyles,
    cursor: 'text',
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
    outline: 'none'
  }

  const handleClick = () => {
    if (!editable || disabled || isEditing) return
    
    setIsEditing(true)
    onEditStart?.(chartId)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    if (newValue.length <= maxLength) {
      setEditValue(newValue)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      finishEditing()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelEditing()
    }
  }

  const finishEditing = () => {
    if (!isEditing) return
    
    const finalTitle = editValue.trim() || placeholder
    setIsEditing(false)
    
    if (finalTitle !== title) {
      onTitleChange?.(chartId, finalTitle)
    }
    
    onEditEnd?.(chartId, finalTitle)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setEditValue(title)
    onEditEnd?.(chartId, title)
  }

  const handleBlur = () => {
    finishEditing()
  }

  const displayTitle = title || placeholder
  const showPlaceholder = !title && !isEditing

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={className}
        style={editingStyles}
        maxLength={maxLength}
        data-testid="chart-title-input"
        data-chart-id={chartId}
      />
    )
  }

  return (
    <div
      className={className}
      style={{
        ...baseStyles,
        opacity: showPlaceholder ? 0.6 : 1,
        fontStyle: showPlaceholder ? 'italic' : 'normal'
      }}
      onClick={handleClick}
      role={editable && !disabled ? 'button' : undefined}
      tabIndex={editable && !disabled ? 0 : undefined}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && editable && !disabled) {
          e.preventDefault()
          handleClick()
        }
      }}
      data-testid="chart-title"
      data-chart-id={chartId}
      title={editable && !disabled ? 'Click to edit' : displayTitle}
    >
      {displayTitle}
    </div>
  )
}

export default ChartTitle 